import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { AdminProviderStatusIndicator } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

const INDICATOR_STYLES: Record<AdminProviderStatusIndicator, { label: string; classes: string }> = {
  none: {
    label: 'Operational',
    classes:
      'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300 border-green-300 dark:border-green-800',
  },
  minor: {
    label: 'Minor incident',
    classes:
      'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 border-amber-300 dark:border-amber-800',
  },
  major: {
    label: 'Major incident',
    classes:
      'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300 border-orange-300 dark:border-orange-800',
  },
  critical: {
    label: 'Critical incident',
    classes:
      'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300 border-red-300 dark:border-red-800',
  },
};

/**
 * The provider's OWN status-page reading, as a chip linking to their page —
 * the triage step between "our error-rate alarm fired" and "blame the
 * provider". Renders nothing while loading or when the provider has no known
 * status page; an unreachable status vendor shows "Status unavailable"
 * (absence of data must not read as health).
 */
export function ProviderStatusChip({ provider }: { provider: string }) {
  const { data } = useQuery({
    queryKey: queryKeys.adminProviders.status(provider),
    // Matches the server's per-provider cache window.
    staleTime: 2 * 60_000,
    queryFn: () => api.adminGetProviderStatus(provider),
  });

  if (!data || !data.supported || !data.status) return null;
  const { status } = data;

  const style =
    status.ok && status.indicator
      ? INDICATOR_STYLES[status.indicator]
      : {
          label: 'Status unavailable',
          classes:
            'bg-muted text-muted-foreground border-border dark:bg-muted dark:text-muted-foreground',
        };

  return (
    <a
      href={status.statusPageUrl}
      target="_blank"
      rel="noopener noreferrer"
      title={
        status.ok
          ? (status.description ?? undefined)
          : `Could not read the provider status page: ${status.error}`
      }
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium hover:opacity-80 ${style.classes}`}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden />
      {style.label}
    </a>
  );
}
